import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { Button } from '../../components/ui/Button'
import { Card, CardHeader } from '../../components/ui/Card'
import { Input } from '../../components/ui/Input'
import { Select } from '../../components/ui/Select'
import { SkeletonFormSections } from '../../components/ui/Skeleton'
import { StatusModal, useStatusModal } from '../../components/ui/StatusModal'
import { useToast } from '../../context/ToastContext'
import {
  PLATFORM_IMPRINT_LOCALES,
  fetchAdminPlatformImprint,
  patchAdminPlatformImprint,
  type PlatformImprintDocument,
  type PlatformImprintLocaleContent,
} from '../../services/platformImprintApi'

const textareaClass =
  'min-h-[4.5rem] w-full resize-y rounded-2xl border border-input bg-background px-4 py-2.5 text-sm text-foreground shadow-sm transition-all placeholder:text-muted-foreground hover:border-border focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring/30 disabled:cursor-not-allowed disabled:opacity-60'

const LOCALE_LABELS: Record<string, string> = {
  en: 'English',
  de: 'Deutsch',
  es: 'Español',
}

function emptyLocaleContent(): PlatformImprintLocaleContent {
  return {
    intro: '',
    company: { name: '', legalForm: '', representedBy: '', onlinePresence: '', address: '' },
    contact: { phones: [''], emails: [''] },
    commercialRegister: { court: '', registerNumber: '', vatId: '', vatNote: '' },
    contentResponsible: { name: '', address: '' },
    euDispute: { showOdrLink: true, body: '' },
    consumerArbitration: { body: '' },
    footerNote: '',
  }
}

function mergeLocaleFromDoc(
  doc: PlatformImprintDocument | null,
  locale: string,
): PlatformImprintLocaleContent {
  const base = emptyLocaleContent()
  const from = doc?.locales?.[locale]
  if (!from) return base
  return {
    ...base,
    ...from,
    company: { ...base.company, ...from.company },
    contact: {
      phones: from.contact?.phones?.length ? from.contact.phones : base.contact?.phones,
      emails: from.contact?.emails?.length ? from.contact.emails : base.contact?.emails,
    },
    commercialRegister: { ...base.commercialRegister, ...from.commercialRegister },
    contentResponsible: { ...base.contentResponsible, ...from.contentResponsible },
    euDispute: { ...base.euDispute, ...from.euDispute },
    consumerArbitration: { ...base.consumerArbitration, ...from.consumerArbitration },
  }
}

function linesToList(s: string): string[] {
  return s
    .split('\n')
    .map((x) => x.trim())
    .filter(Boolean)
}

function listToLines(arr: string[] | undefined): string {
  return (arr ?? []).join('\n')
}

export default function LegalImprintPage() {
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [doc, setDoc] = useState<PlatformImprintDocument | null>(null)
  const [activeLocale, setActiveLocale] = useState<string>('de')
  const [defaultLocale, setDefaultLocale] = useState('de')
  const [form, setForm] = useState<PlatformImprintLocaleContent>(() => emptyLocaleContent())
  const [submitting, setSubmitting] = useState(false)
  const { pushToast } = useToast()
  const { openError, statusModalProps } = useStatusModal()

  const load = useCallback(async () => {
    setLoading(true)
    setLoadError(null)
    try {
      const data = await fetchAdminPlatformImprint()
      setDoc(data)
      setDefaultLocale(data.defaultLocale || 'de')
    } catch (e) {
      setLoadError((e as Error).message || 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    setForm(mergeLocaleFromDoc(doc, activeLocale))
  }, [doc, activeLocale])

  function updateForm(patch: Partial<PlatformImprintLocaleContent>) {
    setForm((prev) => ({ ...prev, ...patch }))
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault()
    setSubmitting(true)
    try {
      const payload: PlatformImprintLocaleContent = {
        ...form,
        contact: {
          phones: linesToList(listToLines(form.contact?.phones)),
          emails: linesToList(listToLines(form.contact?.emails)),
        },
      }
      const updated = await patchAdminPlatformImprint({
        defaultLocale,
        locales: { [activeLocale]: payload },
      })
      setDoc(updated)
      const localeLabel = LOCALE_LABELS[activeLocale] ?? activeLocale
      pushToast({
        message: `${localeLabel} imprint saved and live on storefronts`,
        variant: 'success',
      })
    } catch (err) {
      const message = (err as Error).message || 'Save failed'
      openError({
        title: 'Could not save imprint',
        description: message,
      })
    } finally {
      setSubmitting(false)
    }
  }

  async function handleDefaultLocaleChange(value: string) {
    setDefaultLocale(value)
    try {
      const updated = await patchAdminPlatformImprint({ defaultLocale: value })
      setDoc(updated)
      const localeLabel = LOCALE_LABELS[value] ?? value
      pushToast({
        message: `${localeLabel} is now the default imprint language`,
        variant: 'success',
      })
    } catch (err) {
      const message = (err as Error).message || 'Update failed'
      openError({
        title: 'Could not update default language',
        description: message,
      })
    }
  }

  if (loading) {
    return <SkeletonFormSections />
  }

  if (loadError) {
    return (
      <div className="space-y-4">
        <p className="text-destructive">{loadError}</p>
        <Button type="button" variant="secondary" onClick={() => load()}>
          Retry
        </Button>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold tracking-tight text-foreground">Platform imprint</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Legal imprint (Impressum) shown on all storefronts at /imprint. Edit each language separately.
        </p>
      </div>

      <Card>
        <CardHeader title="Default language" description="Used when a shopper’s language has no content." />
        <div className="px-6 pb-6">
          <Select
            id="defaultLocale"
            label="Default locale"
            value={defaultLocale}
            onChange={(e) => handleDefaultLocaleChange(e.target.value)}
          >
            {PLATFORM_IMPRINT_LOCALES.map((code) => (
              <option key={code} value={code}>
                {LOCALE_LABELS[code] ?? code}
              </option>
            ))}
          </Select>
        </div>
      </Card>

      <div className="flex flex-wrap gap-2 border-b border-border pb-2">
        {PLATFORM_IMPRINT_LOCALES.map((code) => (
          <button
            key={code}
            type="button"
            onClick={() => setActiveLocale(code)}
            className={`rounded-xl px-4 py-2 text-sm font-medium transition-colors ${
              activeLocale === code
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground'
            }`}
          >
            {LOCALE_LABELS[code] ?? code}
            {doc?.locales?.[code] ? (
              <span className="ml-1.5 text-[10px] opacity-80">●</span>
            ) : null}
          </button>
        ))}
      </div>

      <StatusModal {...statusModalProps} />

      <form onSubmit={handleSave} className="space-y-6">
        <Card>
          <CardHeader title="Introduction" description="" />
          <div className="space-y-4 px-6 pb-6">
            <label className="block text-sm font-medium text-foreground">
              Hero intro
              <textarea
                className={`${textareaClass} mt-1.5`}
                value={form.intro ?? ''}
                onChange={(e) => updateForm({ intro: e.target.value })}
                rows={3}
              />
            </label>
          </div>
        </Card>

        <Card>
          <CardHeader title="Company" description="" />
          <div className="grid gap-4 px-6 pb-6 sm:grid-cols-2">
            <Input
              id="companyName"
              label="Company name"
              value={form.company?.name ?? ''}
              onChange={(e) => updateForm({ company: { ...form.company, name: e.target.value } })}
            />
            <Input
              id="legalForm"
              label="Legal form"
              value={form.company?.legalForm ?? ''}
              onChange={(e) => updateForm({ company: { ...form.company, legalForm: e.target.value } })}
            />
            <Input
              id="representedBy"
              label="Represented by"
              className="sm:col-span-2"
              value={form.company?.representedBy ?? ''}
              onChange={(e) => updateForm({ company: { ...form.company, representedBy: e.target.value } })}
            />
            <Input
              id="onlinePresence"
              label="Online presence"
              className="sm:col-span-2"
              value={form.company?.onlinePresence ?? ''}
              onChange={(e) => updateForm({ company: { ...form.company, onlinePresence: e.target.value } })}
            />
            <label className="block text-sm font-medium text-foreground sm:col-span-2">
              Address
              <textarea
                className={`${textareaClass} mt-1.5`}
                value={form.company?.address ?? ''}
                onChange={(e) => updateForm({ company: { ...form.company, address: e.target.value } })}
                rows={3}
              />
            </label>
          </div>
        </Card>

        <Card>
          <CardHeader title="Contact" description="" />
          <div className="grid gap-4 px-6 pb-6 sm:grid-cols-2">
            <label className="block text-sm font-medium text-foreground">
              Phone numbers (one per line)
              <textarea
                className={`${textareaClass} mt-1.5`}
                value={listToLines(form.contact?.phones)}
                onChange={(e) =>
                  updateForm({ contact: { ...form.contact, phones: linesToList(e.target.value) } })
                }
                rows={3}
              />
            </label>
            <label className="block text-sm font-medium text-foreground">
              Email addresses (one per line)
              <textarea
                className={`${textareaClass} mt-1.5`}
                value={listToLines(form.contact?.emails)}
                onChange={(e) =>
                  updateForm({ contact: { ...form.contact, emails: linesToList(e.target.value) } })
                }
                rows={3}
              />
            </label>
          </div>
        </Card>

        <Card>
          <CardHeader title="Commercial register" description="" />
          <div className="grid gap-4 px-6 pb-6 sm:grid-cols-2">
            <Input
              id="registerCourt"
              label="Register court"
              value={form.commercialRegister?.court ?? ''}
              onChange={(e) =>
                updateForm({
                  commercialRegister: { ...form.commercialRegister, court: e.target.value },
                })
              }
            />
            <Input
              id="registerNumber"
              label="Register number"
              value={form.commercialRegister?.registerNumber ?? ''}
              onChange={(e) =>
                updateForm({
                  commercialRegister: { ...form.commercialRegister, registerNumber: e.target.value },
                })
              }
            />
            <Input
              id="vatId"
              label="VAT ID"
              value={form.commercialRegister?.vatId ?? ''}
              onChange={(e) =>
                updateForm({
                  commercialRegister: { ...form.commercialRegister, vatId: e.target.value },
                })
              }
            />
            <Input
              id="vatNote"
              label="VAT note"
              value={form.commercialRegister?.vatNote ?? ''}
              onChange={(e) =>
                updateForm({
                  commercialRegister: { ...form.commercialRegister, vatNote: e.target.value },
                })
              }
            />
          </div>
        </Card>

        <Card>
          <CardHeader title="Responsible for content" description="" />
          <div className="grid gap-4 px-6 pb-6">
            <Input
              id="responsibleName"
              label="Name"
              value={form.contentResponsible?.name ?? ''}
              onChange={(e) =>
                updateForm({
                  contentResponsible: { ...form.contentResponsible, name: e.target.value },
                })
              }
            />
            <label className="block text-sm font-medium text-foreground">
              Address
              <textarea
                className={`${textareaClass} mt-1.5`}
                value={form.contentResponsible?.address ?? ''}
                onChange={(e) =>
                  updateForm({
                    contentResponsible: { ...form.contentResponsible, address: e.target.value },
                  })
                }
                rows={2}
              />
            </label>
          </div>
        </Card>

        <Card>
          <CardHeader title="EU dispute & arbitration" description="" />
          <div className="space-y-4 px-6 pb-6">
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={form.euDispute?.showOdrLink !== false}
                onChange={(e) =>
                  updateForm({
                    euDispute: { ...form.euDispute, showOdrLink: e.target.checked },
                  })
                }
                className="rounded border-input"
              />
              Show EU ODR platform link
            </label>
            <label className="block text-sm font-medium text-foreground">
              EU dispute text
              <textarea
                className={`${textareaClass} mt-1.5`}
                value={form.euDispute?.body ?? ''}
                onChange={(e) => updateForm({ euDispute: { ...form.euDispute, body: e.target.value } })}
                rows={3}
              />
            </label>
            <label className="block text-sm font-medium text-foreground">
              Consumer arbitration text
              <textarea
                className={`${textareaClass} mt-1.5`}
                value={form.consumerArbitration?.body ?? ''}
                onChange={(e) => updateForm({ consumerArbitration: { body: e.target.value } })}
                rows={3}
              />
            </label>
            <label className="block text-sm font-medium text-foreground">
              Footer compliance note
              <textarea
                className={`${textareaClass} mt-1.5`}
                value={form.footerNote ?? ''}
                onChange={(e) => updateForm({ footerNote: e.target.value })}
                rows={2}
              />
            </label>
          </div>
        </Card>

        <Button type="submit" disabled={submitting}>
          {submitting ? 'Saving…' : `Save ${LOCALE_LABELS[activeLocale] ?? activeLocale}`}
        </Button>
      </form>
    </div>
  )
}
